import { create } from "zustand";
import type { TAnalyticsData, TSessionAnalytics } from "@/types/TAnalytics";
import { getAnalytics, getSessionAnalytics } from "@/services/analytics/getAnalytics";

type AnalyticsState = {
  data: TAnalyticsData | null;
  sessionData: TSessionAnalytics | null;
  isLoading: boolean;
  isSessionLoading: boolean;
  error: string | null;
  selectedDays: number;
  
  fetchAnalytics: (days?: number) => Promise<void>;
  setSelectedDays: (days: number) => void;
  reset: () => void;
};

const initialState = {
  data: null,
  sessionData: null,
  isLoading: false,
  isSessionLoading: false,
  error: null,
  selectedDays: 30,
};

export const useAnalyticsStore = create<AnalyticsState>((set, get) => ({
  ...initialState,

  fetchAnalytics: async (days?: number) => {
    const daysToFetch = days ?? get().selectedDays;
    set({ isLoading: true, isSessionLoading: true, error: null });

    // Load Cosmos analytics first; HeyGen session stats often take much longer
    // and must not block the main dashboard.
    try {
      const data = await getAnalytics(daysToFetch);
      set({ data, isLoading: false, selectedDays: daysToFetch });
    } catch (error) {
      console.error("Error fetching analytics:", error);
      set({
        error: error instanceof Error ? error.message : "Fehler beim Laden der Analytics",
        isLoading: false,
        isSessionLoading: false,
      });
      return;
    }

    try {
      const sessionData = await getSessionAnalytics(daysToFetch);
      set({ sessionData, isSessionLoading: false });
    } catch (error) {
      console.error("Error fetching session analytics:", error);
      set({ sessionData: null, isSessionLoading: false });
    }
  },

  setSelectedDays: (days: number) => {
    set({ selectedDays: days });
    get().fetchAnalytics(days);
  },

  reset: () => set(initialState),
}));
